
import { useState, useEffect, useCallback } from 'react'
import { messagesApi } from '../lib/api'
import { useAuth } from './useAuth'
import { mockConversations, mockMessages } from '../mocks/conversations'

export type Conversation = {
  id: string
  created_at: string
  updated_at: string
  is_group: boolean
  group_name?: string
  group_icon?: string
  participants: ConversationParticipant[]
  last_message?: Message
  unread_count: number
}

export type ConversationParticipant = {
  id: string
  conversation_id: string
  user_id: string
  joined_at: string
  last_read_at: string
  is_admin?: boolean
  user_profile?: {
    id?: string
    username: string
    avatar_url: string | null
  }
}

export type Message = {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  created_at: string
  updated_at: string
  is_deleted: boolean
  sender?: {
    username: string
    avatar_url: string | null
  }
}

// Local storage for mock data persistence
const MOCK_STORAGE_KEY = 'filmora_mock_messages'
const MOCK_CONV_STORAGE_KEY = 'filmora_mock_conversations'

const loadMockMessages = (): Record<string, any[]> => {
  try {
    const stored = localStorage.getItem(MOCK_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Error loading mock messages:', e)
  }
  return { ...mockMessages }
}

const saveMockMessages = (messages: Record<string, any[]>) => {
  try {
    localStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(messages))
  } catch (e) {
    console.error('Error saving mock messages:', e)
  }
}

const loadMockConversations = (): any[] => {
  try {
    const stored = localStorage.getItem(MOCK_CONV_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Error loading mock conversations:', e)
  }
  return [...mockConversations]
}

const saveMockConversations = (conversations: any[]) => {
  try {
    localStorage.setItem(MOCK_CONV_STORAGE_KEY, JSON.stringify(conversations))
  } catch (e) {
    console.error('Error saving mock conversations:', e)
  }
}

export const useMessages = () => {
  const { user, loading: authLoading } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localMockMessages, setLocalMockMessages] = useState<Record<string, any[]>>(loadMockMessages)
  const [localMockConversations, setLocalMockConversations] = useState<any[]>(loadMockConversations)

  // Fetch conversations via API
  const fetchConversations = useCallback(async () => {
    if (authLoading) {
      return
    }

    if (!user) {
      // Use mock data when not authenticated
      const mockData = loadMockConversations()
      setLocalMockConversations(mockData)
      setConversations(mockData as any)
      setLoading(false)
      return
    }

    try {
      setError(null)
      
      const response = await messagesApi.getConversations()

      if (!response.success || !response.data || response.data.length === 0) {
        // Use mock data when no real conversations exist
        const mockData = loadMockConversations()
        setLocalMockConversations(mockData)
        setConversations(mockData as any)
        setLoading(false)
        return
      }

      setConversations(response.data)
    } catch (error) {
      console.error('Error fetching conversations:', error)
      setError('Failed to load conversations')
      const mockData = loadMockConversations()
      setLocalMockConversations(mockData)
      setConversations(mockData as any)
    } finally {
      setLoading(false)
    }
  }, [user, authLoading])

  // Fetch messages for a conversation via API
  const fetchMessages = useCallback(async (conversationId: string) => {
    if (!conversationId) return

    try {
      setMessagesLoading(true)
      setError(null)

      // If user is not authenticated or conversation is a mock one, use mock data
      if (!user || conversationId.startsWith('conv-') || conversationId.startsWith('group-')) {
        const allMockMsgs = loadMockMessages()
        const mockMsgs = allMockMsgs[conversationId] || []
        setMessages(mockMsgs.filter((m: any) => !m.is_deleted) as any)
        setMessagesLoading(false)
        return
      }

      const response = await messagesApi.getMessages(conversationId)

      if (!response.success || !response.data || response.data.length === 0) {
        const allMockMsgs = loadMockMessages()
        const mockMsgs = allMockMsgs[conversationId] || []
        setMessages(mockMsgs.filter((m: any) => !m.is_deleted) as any)
        setMessagesLoading(false)
        return
      }

      setMessages(response.data)
    } catch (error) {
      console.error('Error fetching messages:', error)
      setError('Failed to load messages')
      const allMockMsgs = loadMockMessages()
      const mockMsgs = allMockMsgs[conversationId] || []
      setMessages(mockMsgs.filter((m: any) => !m.is_deleted) as any)
    } finally {
      setMessagesLoading(false)
    }
  }, [user])

  // Send a message via API
  const sendMessage = async (conversationId: string, content: string) => {
    if (!content.trim()) return

    const tempId = `temp-${Date.now()}`
    const now = new Date().toISOString()
    const senderId = user?.id || 'user-1'
    
    const tempMessage = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: senderId,
      content: content.trim(),
      created_at: now,
      updated_at: now,
      is_deleted: false,
      sender: {
        username: user?.user_metadata?.username || 'You',
        avatar_url: user?.user_metadata?.avatar_url || null
      }
    }

    // Optimistic update
    setMessages(prev => [...prev, tempMessage])

    // If using mock data (not authenticated or mock conversation)
    if (!user || conversationId.startsWith('conv-') || conversationId.startsWith('group-')) {
      const newMessage = {
        ...tempMessage,
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }
      
      // Update local mock messages
      const updatedMockMessages = { ...localMockMessages }
      if (!updatedMockMessages[conversationId]) {
        updatedMockMessages[conversationId] = []
      }
      updatedMockMessages[conversationId] = [...updatedMockMessages[conversationId], newMessage]
      setLocalMockMessages(updatedMockMessages)
      saveMockMessages(updatedMockMessages)
      
      // Update conversation's last message
      const updatedConversations = localMockConversations.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            updated_at: now,
            last_message: {
              id: newMessage.id,
              content: newMessage.content,
              sender_id: newMessage.sender_id,
              created_at: newMessage.created_at
            }
          }
        }
        return conv
      })
      updatedConversations.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      
      setMessages(prev => prev.map(msg => msg.id === tempId ? newMessage : msg))
      
      return newMessage
    }

    try {
      const response = await messagesApi.sendMessage(conversationId, content.trim())

      if (!response.success) {
        throw new Error(response.error || 'Failed to send message')
      }

      // Replace temp message with real message
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? response.data : msg
      ))

      // Refresh conversations to update last message
      setTimeout(() => fetchConversations(), 500)

      return response.data
    } catch (error) {
      console.error('Error sending message:', error)
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId))
      throw error
    }
  }

  // Delete a message via API
  const deleteMessage = async (messageId: string, conversationId: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('conv-') || conversationId.startsWith('group-')) {
      const updatedMockMessages = { ...localMockMessages }
      if (updatedMockMessages[conversationId]) {
        updatedMockMessages[conversationId] = updatedMockMessages[conversationId].map((msg: any) => 
          msg.id === messageId ? { ...msg, is_deleted: true } : msg
        )
        setLocalMockMessages(updatedMockMessages)
        saveMockMessages(updatedMockMessages)
        
        setMessages(prev => prev.filter(msg => msg.id !== messageId))
        
        const remainingMessages = updatedMockMessages[conversationId].filter((m: any) => !m.is_deleted)
        const lastMsg = remainingMessages[remainingMessages.length - 1]
        
        const updatedConversations = localMockConversations.map(conv => {
          if (conv.id === conversationId) {
            return {
              ...conv,
              last_message: lastMsg ? {
                id: lastMsg.id,
                content: lastMsg.content,
                sender_id: lastMsg.sender_id,
                created_at: lastMsg.created_at
              } : undefined
            }
          }
          return conv
        })
        setLocalMockConversations(updatedConversations)
        saveMockConversations(updatedConversations)
        setConversations(updatedConversations as any)
      }
      return
    }

    try {
      const response = await messagesApi.deleteMessage(messageId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete message')
      }

      setMessages(prev => prev.filter(msg => msg.id !== messageId))
      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error deleting message:', error)
      throw error
    }
  }

  // Start a new conversation via API
  const startConversation = async (otherUserId: string) => {
    // If using mock data
    if (!user) {
      const existingConv = localMockConversations.find(conv => 
        !conv.is_group && 
        conv.participants.some((p: any) => p.user_id === otherUserId)
      )
      
      if (existingConv) {
        return existingConv.id
      }
      
      const newConvId = `conv-new-${Date.now()}`
      const now = new Date().toISOString()
      
      let otherUserProfile = null
      for (const conv of localMockConversations) {
        const participant = conv.participants.find((p: any) => p.user_id === otherUserId)
        if (participant) {
          otherUserProfile = participant.user_profile
          break
        }
      }
      
      const newConversation = {
        id: newConvId,
        created_at: now,
        updated_at: now,
        is_group: false,
        unread_count: 0,
        participants: [
          {
            user_id: 'user-1',
            user_profile: { id: 'user-1', username: 'You', avatar_url: '' }
          },
          {
            user_id: otherUserId,
            user_profile: otherUserProfile || { id: otherUserId, username: 'New User', avatar_url: '' }
          }
        ]
      }
      
      const updatedConversations = [newConversation, ...localMockConversations]
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      
      const updatedMockMessages = { ...localMockMessages, [newConvId]: [] }
      setLocalMockMessages(updatedMockMessages)
      saveMockMessages(updatedMockMessages)
      
      return newConvId
    }

    try {
      const response = await messagesApi.createConversation([otherUserId])

      if (!response.success) {
        throw new Error(response.error || 'Failed to create conversation')
      }

      setTimeout(() => fetchConversations(), 500)
      return response.data.id
    } catch (error) {
      console.error('Error starting conversation:', error)
      throw error
    }
  }

  // Create a group conversation via API
  const createGroupConversation = async (groupName: string, memberIds: string[], groupIcon?: string) => {
    const MAX_MEMBERS = 100
    if (memberIds.length > MAX_MEMBERS - 1) {
      throw new Error(`Maximum ${MAX_MEMBERS} members allowed`)
    }

    // If using mock data
    if (!user) {
      const newGroupId = `group-new-${Date.now()}`
      const now = new Date().toISOString()
      
      const memberProfiles: any[] = [{
        user_id: 'user-1',
        is_admin: true,
        user_profile: { id: 'user-1', username: 'You', avatar_url: '' }
      }]
      
      for (const memberId of memberIds) {
        let profile = null
        for (const conv of localMockConversations) {
          const participant = conv.participants.find((p: any) => p.user_id === memberId)
          if (participant) {
            profile = participant.user_profile
            break
          }
        }
        memberProfiles.push({
          user_id: memberId,
          is_admin: false,
          user_profile: profile || { id: memberId, username: `User ${memberId}`, avatar_url: '' }
        })
      }
      
      const systemMessage = {
        id: `gmsg-${Date.now()}`,
        conversation_id: newGroupId,
        sender_id: 'user-1',
        content: `Created group "${groupName}"`,
        created_at: now,
        is_deleted: false
      }
      
      const newGroup = {
        id: newGroupId,
        created_at: now,
        updated_at: now,
        is_group: true,
        group_name: groupName,
        group_icon: groupIcon || 'ri-group-line',
        unread_count: 0,
        last_message: {
          id: systemMessage.id,
          content: systemMessage.content,
          sender_id: systemMessage.sender_id,
          created_at: systemMessage.created_at
        },
        participants: memberProfiles
      }
      
      const updatedConversations = [newGroup, ...localMockConversations]
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      
      const updatedMockMessages = { ...localMockMessages, [newGroupId]: [systemMessage] }
      setLocalMockMessages(updatedMockMessages)
      saveMockMessages(updatedMockMessages)
      
      return newGroupId
    }

    try {
      const response = await messagesApi.createGroupConversation({
        name: groupName,
        memberIds,
        icon: groupIcon
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to create group')
      }

      setTimeout(() => fetchConversations(), 500)
      return response.data.id
    } catch (error) {
      console.error('Error creating group conversation:', error)
      throw error
    }
  }

  // Add members to group via API
  const addGroupMembers = async (conversationId: string, memberIds: string[]) => {
    if (!user) return

    try {
      const response = await messagesApi.inviteMembers(conversationId, memberIds)

      if (!response.success) {
        throw new Error(response.error || 'Failed to add members')
      }

      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error adding group members:', error)
      throw error
    }
  }

  // Leave group via API
  const leaveGroup = async (conversationId: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('group-')) {
      const updatedConversations = localMockConversations.filter(conv => conv.id !== conversationId)
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      
      if (currentConversation === conversationId) {
        setCurrentConversation(null)
      }
      return
    }

    try {
      const response = await messagesApi.leaveConversation(conversationId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to leave group')
      }

      if (currentConversation === conversationId) {
        setCurrentConversation(null)
      }

      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error leaving group:', error)
      throw error
    }
  }

  // Remove member from group via API
  const removeMember = async (conversationId: string, memberUserId: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('group-')) {
      const updatedConversations = localMockConversations.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            participants: conv.participants.filter((p: any) => p.user_id !== memberUserId)
          }
        }
        return conv
      })
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      return
    }

    try {
      const response = await messagesApi.removeMember(conversationId, memberUserId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to remove member')
      }

      setConversations(prev => prev.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            participants: conv.participants.filter(p => p.user_id !== memberUserId)
          }
        }
        return conv
      }))

      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error removing member:', error)
      throw error
    }
  }

  // Update group settings via API
  const updateGroupSettings = async (conversationId: string, groupName: string, groupIcon: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('group-')) {
      const updatedConversations = localMockConversations.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            group_name: groupName,
            group_icon: groupIcon,
            updated_at: new Date().toISOString()
          }
        }
        return conv
      })
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      return
    }

    try {
      const response = await messagesApi.updateGroupSettings(conversationId, {
        name: groupName,
        icon: groupIcon
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to update settings')
      }

      setConversations(prev => prev.map(conv => {
        if (conv.id === conversationId) {
          return { ...conv, group_name: groupName, group_icon: groupIcon }
        }
        return conv
      }))
    } catch (error) {
      console.error('Error updating group settings:', error)
      throw error
    }
  }

  // Invite members to existing group via API
  const inviteMembers = async (conversationId: string, memberIds: string[]) => {
    // If using mock data
    if (!user || conversationId.startsWith('group-')) {
      const newMembers: any[] = []
      for (const memberId of memberIds) {
        let profile = null
        for (const conv of localMockConversations) {
          const participant = conv.participants.find((p: any) => p.user_id === memberId)
          if (participant) {
            profile = participant.user_profile
            break
          }
        }
        newMembers.push({
          user_id: memberId,
          is_admin: false,
          user_profile: profile || { id: memberId, username: `User ${memberId}`, avatar_url: '' }
        })
      }
      
      const updatedConversations = localMockConversations.map(conv => {
        if (conv.id === conversationId) {
          return { ...conv, participants: [...conv.participants, ...newMembers] }
        }
        return conv
      })
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      return
    }

    try {
      const response = await messagesApi.inviteMembers(conversationId, memberIds)

      if (!response.success) {
        throw new Error(response.error || 'Failed to invite members')
      }

      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error inviting members:', error)
      throw error
    }
  }

  // Delete conversation via API
  const deleteConversation = async (conversationId: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('conv-') || conversationId.startsWith('group-')) {
      const updatedConversations = localMockConversations.filter(conv => conv.id !== conversationId)
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      
      const updatedMockMessages = { ...localMockMessages }
      delete updatedMockMessages[conversationId]
      setLocalMockMessages(updatedMockMessages)
      saveMockMessages(updatedMockMessages)
      
      if (currentConversation === conversationId) {
        setCurrentConversation(null)
      }
      return
    }

    try {
      const response = await messagesApi.leaveConversation(conversationId)

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete conversation')
      }

      if (currentConversation === conversationId) {
        setCurrentConversation(null)
      }

      setTimeout(() => fetchConversations(), 500)
    } catch (error) {
      console.error('Error deleting conversation:', error)
      throw error
    }
  }

  // Mark conversation as read via API
  const markAsRead = async (conversationId: string) => {
    // If using mock data
    if (!user || conversationId.startsWith('conv-') || conversationId.startsWith('group-')) {
      const updatedConversations = localMockConversations.map(conv => 
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      )
      setLocalMockConversations(updatedConversations)
      saveMockConversations(updatedConversations)
      setConversations(updatedConversations as any)
      return
    }

    try {
      await messagesApi.markAsRead(conversationId)

      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      ))
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  // Initialize conversations when auth is ready
  useEffect(() => {
    if (!authLoading) {
      fetchConversations()
    }
  }, [authLoading, fetchConversations])

  // Fetch messages when conversation changes
  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation)
      markAsRead(currentConversation)
    } else {
      setMessages([])
    }
  }, [currentConversation, fetchMessages])

  // Poll for new messages periodically
  useEffect(() => {
    if (!user || !currentConversation) return

    const pollInterval = setInterval(() => {
      fetchMessages(currentConversation)
      fetchConversations()
    }, 10000) // Poll every 10 seconds

    return () => clearInterval(pollInterval)
  }, [user, currentConversation, fetchMessages, fetchConversations])

  return {
    conversations,
    messages,
    currentConversation,
    loading: loading || authLoading,
    messagesLoading,
    error,
    setCurrentConversation,
    sendMessage,
    deleteMessage,
    deleteConversation,
    startConversation,
    createGroupConversation,
    addGroupMembers,
    leaveGroup,
    removeMember,
    updateGroupSettings,
    inviteMembers,
    markAsRead,
    fetchConversations
  }
}
